import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useCenter } from "../modules/center/application/useCenter";
import QuickRecordModal from "../modules/session/ui/components/QuickRecordModal";
import { BarChart3, Building2, Mic, Settings, Users, Puzzle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SidebarPatientSearch from "./SidebarPatientSearch";

// Assume these icons are imported from an icon library
import {
  ChevronDownIcon,
  GridIcon,
  HorizontaLDots,
} from "../icons";
import { useSidebar } from "../context/useSidebar";
import { useSubscription } from "../modules/subscription/application/useSubscription";
import { useAdoptedTools } from "../modules/marketplace/application/useMarketplace";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { visibleToolNavItems } from "./toolNavItems";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const navItems: NavItem[] = [
  {
    icon: <GridIcon />,
    name: "sidebar.home",
    path: "/",
  },
  {
    name: "sidebar.patients",
    icon: <Users size={20} />,
    path: "patients",
  },
  // Every practitioner's own reports: a centre's therapist or mentor, and a
  // therapist on their own. Placed after Patients and the tools beside them.
  {
    name: "sidebar.reports",
    icon: <BarChart3 size={20} />,
    path: "reports",
  },
  // Sessions and Therapeutic Plans are intentionally not in the main nav —
  // they are accessed from within a patient's view to keep the menu simple.
  // Their routes still exist.
  {
    name: "sidebar.toolsCatalog",
    icon: <Puzzle size={20} />,
    path: "tools-catalog",
  },
  {
    name: "sidebar.settings",
    icon: <Settings size={20} />,
    path: "settings",
  },
];

const AppSidebar: React.FC = () => {
  const { t } = useTranslation();
  const { isExpanded, isMobileOpen, isHovered } = useSidebar();
  const { isPremium } = useSubscription();
  const location = useLocation();

  const [showQuickRecord, setShowQuickRecord] = useState(false);

  // Entries that only exist once the therapist adopts the matching tool.
  // Both sit directly under Patients: booking a slot and billing for it are
  // things you do with a patient, so they belong beside them rather than down
  // by Settings. Listed in the order they appear there.
  const { data: adoptedTools } = useAdoptedTools();
  // An account can hold both roles — running a centre and seeing patients in
  // it is the ordinary case for a small practice. This is the way across:
  // the two surfaces are places, not modes, so switching is a link rather
  // than a state anybody has to remember they are in.
  const { isAdmin } = useCenter();
  const visibleNavItems = useMemo(() => {
    const toolItems = visibleToolNavItems(
      (adoptedTools ?? []).map((tool) => tool.slug)
    );

    const patientsIndex = navItems.findIndex((item) => item.path === "patients");
    const insertAt = patientsIndex >= 0 ? patientsIndex + 1 : navItems.length;
    const withTools =
      toolItems.length === 0
        ? [...navItems]
        : [
            ...navItems.slice(0, insertAt),
            ...toolItems,
            ...navItems.slice(insertAt),
          ];

    if (isAdmin) {
      // Right after Home: for an administrator the centre is the other place
      // they work, not an appendix at the bottom of the menu.
      const homeIndex = withTools.findIndex((item) => item.path === "/");
      withTools.splice(homeIndex + 1, 0, {
        name: "sidebar.center",
        icon: <Building2 size={20} />,
        path: "center",
      });
      return withTools;
    }

    // A member who does not administer has no centre entry here: their centre
    // business — its details, and the people they brought with them — lives
    // in the profile's Centre tab, reached from the user menu.
    return withTools;
  }, [adoptedTools, isAdmin]);

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);

  const isActive = useCallback(
    (path: string) => {
      if (path === "/") return location.pathname === "/";
      return location.pathname.startsWith(`/${path}`);
    },
    [location.pathname]
  );

  useEffect(() => {
    let submenuMatched = false;
    navItems.forEach((nav, index) => {
      if (nav.subItems) {
        nav.subItems.forEach((subItem) => {
          if (isActive(subItem.path)) {
            setOpenSubmenu({
              type: "main",
              index,
            });
            submenuMatched = true;
          }
        });
      }
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive]);


  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const menuTransition = { type: "spring" as const, stiffness: 300, damping: 30 };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className="flex flex-col gap-1.5">
      {/* New Live Session Button - Only for main menu and Premium users */}
      {menuType === "main" && isPremium && (
        <li className="mb-4">
          <button
            onClick={() => setShowQuickRecord(true)}
            aria-label={t("sidebar.newLiveSession")}
            className={`flex min-h-11 w-full items-center gap-3 rounded-lg bg-accent font-semibold text-accent-contrast
              transition-colors duration-[--duration-fast] ease-[--ease-standard] hover:bg-accent-hover
              ${!isExpanded && !isHovered ? "justify-center px-2" : "px-4 py-2.5"}`}
          >
            <Mic aria-hidden="true" className="size-5 shrink-0" />
            {(isExpanded || isHovered || isMobileOpen) && (
              <span className="overflow-hidden whitespace-nowrap text-sm">
                {t("sidebar.newLiveSession")}
              </span>
            )}
          </button>
        </li>
      )}

      {/* Patient Search Filter - Only for main menu */}
      {menuType === "main" && (
        <SidebarPatientSearch />
      )}

      {items.map((nav, index) => {
        const isRestricted = nav.path === "therapeutic-plans" && !isPremium;
        if (isRestricted) return null;

        return (
          <li key={nav.name}>
            {nav.subItems ? (
              <>
                <button
                  onClick={() => handleSubmenuToggle(index, menuType)}
                  className={`menu-item group ${openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-active"
                    : "menu-item-inactive"
                    } cursor-pointer ${!isExpanded && !isHovered
                      ? "lg:justify-center"
                      : "lg:justify-start"
                    }`}
                >
                  <span
                    className={`menu-item-icon-size  ${openSubmenu?.type === menuType && openSubmenu?.index === index
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                      }`}
                  >
                    {nav.icon}
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className="menu-item-text font-medium">{t(nav.name)}</span>
                  )}
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <ChevronDownIcon
                      className={`ml-auto w-4 h-4 transition-transform duration-[--duration-base] ${openSubmenu?.type === menuType &&
                        openSubmenu?.index === index
                        ? "rotate-180 text-accent-on-tint"
                        : "text-content-secondary"
                        }`}
                    />
                  )}
                </button>
                <AnimatePresence>
                  {(isExpanded || isHovered || isMobileOpen) && openSubmenu?.type === menuType && openSubmenu?.index === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={menuTransition}
                      className="overflow-hidden"
                    >
                      <ul className="mt-1 space-y-1 ml-9">
                        {nav.subItems.map((subItem) => (
                          <li key={subItem.name}>
                            <Link
                              to={subItem.path}
                              className={`menu-dropdown-item ${isActive(subItem.path)
                                ? "menu-dropdown-item-active"
                                : "menu-dropdown-item-inactive"
                                }`}
                            >
                              {t(subItem.name)}
                              <span className="flex items-center gap-1 ml-auto">
                                {subItem.new && (
                                  <span className="menu-dropdown-badge menu-dropdown-badge-active">
                                    new
                                  </span>
                                )}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              nav.path && (
                <Link
                  to={nav.path}
                  data-tour={`sidebar-${nav.path}`}
                  aria-current={isActive(nav.path) ? "page" : undefined}
                  className={`menu-item group ${isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                    }`}
                >
                  <span
                    className={`menu-item-icon-size transition-colors duration-200 ${isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                      }`}
                  >
                    {nav.icon}
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className="menu-item-text font-medium">{t(nav.name)}</span>
                  )}
                </Link>
              )
            )}
          </li>
        );
      })}
    </ul>
  );

  const { isSmallScreen } = useBreakpoint();

  return (
    <>
      <aside
        className={`fixed flex flex-col top-0 left-0 h-screen transition-[width,transform] duration-[--duration-slow] ease-[--ease-standard] z-50 bg-surface-raised border-r border-border-subtle
          ${isExpanded || isMobileOpen
            ? "w-[280px]"
            : isHovered
              ? "w-[280px]"
              : "w-[88px]"
          }
          ${isSmallScreen
            ? (isMobileOpen ? "translate-x-0" : "-translate-x-full")
            : "translate-x-0"}
          px-4`}
      >
        <div
          className={`h-16 flex items-center ${!isExpanded && !isHovered ? "lg:justify-center" : "justify-start px-2"
            }`}
        >
          <Link to="/" className="hover:opacity-80 transition-opacity">
            {isExpanded || isHovered || isMobileOpen ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={menuTransition}
              >
                {/* One image, whitened in the dark. This used to be two —
                    `dark:hidden` and `hidden dark:block` — both pointing at
                    the same file, which is the shape of a swap whose second
                    asset never existed: the wordmark is drawn in the brand's
                    navy, and on a dark sidebar it disappeared. */}
                <img
                  className="dark:brightness-0 dark:invert"
                  src="/images/logo/logo.png"
                  alt="Alchely"
                  width={130}
                  height={32}
                />
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={menuTransition}
              >
                <img
                  src="/images/logo/icon.png"
                  alt="Logo"
                  width={34}
                  height={34}
                />
              </motion.div>
            )}
          </Link>
        </div>

        <div className="flex flex-col flex-1 overflow-y-auto no-scrollbar py-4">
          <nav className="space-y-8">
            <div>
              {(isExpanded || isHovered || isMobileOpen) && (
                <h2 className="mb-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
                  {t("sidebar.menu")}
                </h2>
              )}
              {!isExpanded && !isHovered && !isMobileOpen && (
                <div className="flex justify-center mb-6">
                  <HorizontaLDots className="size-5 text-content-tertiary" />
                </div>
              )}
              {renderMenuItems(visibleNavItems, "main")}
            </div>
          </nav>
        </div>

        {!isPremium && (
          <Link to="/subscription" className="py-6 mt-auto block group/plan">
            <div className={`rounded-lg border border-accent/20 bg-accent-tint p-4 transition-colors duration-[--duration-fast] group-hover/plan:border-accent/40
              ${!isExpanded && !isHovered ? "invisible h-0 opacity-0" : "visible opacity-100"}`}
            >
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-accent-on-tint">{t("sidebar.plusPlan")}</p>
              <p className="text-sm leading-relaxed text-content-secondary">{t("sidebar.plusPlanDesc")}</p>
            </div>
          </Link>
        )}
      </aside>

      {/* QUICK RECORD MODAL */}
      {showQuickRecord && (
        <QuickRecordModal onClose={() => setShowQuickRecord(false)} />
      )}
    </>
  );
};

export default AppSidebar;
